/**
 * Search and filters for the Creators dashboard list (pure — unit tested).
 *
 * A query matches when EVERY whitespace-separated word appears (case- and
 * accent-insensitive) somewhere on the dashboard: its label, slug or creator,
 * or any channel's name, UC id or ChannelMeter payee (often the agency). A
 * pasted dashboard link searches its slug; a pasted youtube.com/channel/UC…
 * link searches its channel id. When the hit came from a channel rather than
 * the dashboard's own label, the row reports which channel matched.
 */

import { CreatorPageChannelLabel, CreatorReportPage } from './types';

export type DashboardTypeFilter = 'all' | 'creator' | 'agency';
export type DashboardStatusFilter = 'any' | 'active' | 'deactivated' | 'locked';

export const TYPE_FILTERS: DashboardTypeFilter[] = ['all', 'creator', 'agency'];
export const STATUS_FILTERS: DashboardStatusFilter[] = ['any', 'active', 'deactivated', 'locked'];

export interface DashboardFilters {
  q: string;
  type: DashboardTypeFilter;
  status: DashboardStatusFilter;
}

export interface DashboardRow {
  page: CreatorReportPage;
  /** The channel that satisfied the search when the dashboard's own label/slug didn't. */
  matchedChannel?: CreatorPageChannelLabel;
}

export interface FilteredDashboards {
  rows: DashboardRow[];
  /** Per-type counts under the current search + status (for the type tabs). */
  counts: Record<DashboardTypeFilter, number>;
}

export const fold = (s: string): string =>
  s
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

export const isLocked = (p: Pick<CreatorReportPage, 'lockedUntil'>, now: Date = new Date()): boolean =>
  !!p.lockedUntil && p.lockedUntil > now.toISOString();

/** Labels from the list response, or — from an older backend — stored names and ids. */
export const channelLabelsOf = (p: CreatorReportPage): CreatorPageChannelLabel[] =>
  p.channelLabels ??
  p.channelIds.map((id) => ({ channelId: id, channelName: p.channelNames?.[id] || id }));

/** Words to match. A pasted dashboard or channel link reduces to the one part a row can match. */
export function searchTerms(q: string): string[] {
  const trimmed = q.trim();
  const dashboard = trimmed.match(/\/creators\/([a-z0-9]+(?:-[a-z0-9]+)*)(?:[/?#]|$)/i);
  if (dashboard) return [fold(dashboard[1])];
  const channel = trimmed.match(/youtube\.com\/channel\/(UC[\w-]{22})/i);
  if (channel) return [fold(channel[1])];
  return fold(trimmed).split(/\s+/).filter(Boolean);
}

function matchRow(page: CreatorReportPage, terms: string[]): DashboardRow | null {
  if (terms.length === 0) return { page };
  const own = fold([page.clientLabel, page.slug, page.createdBy].join(' '));
  const channels = channelLabelsOf(page).map((c) => ({
    label: c,
    text: fold([c.channelName, c.channelId, c.payeeName ?? ''].join(' ')),
  }));
  for (const t of terms) {
    if (!own.includes(t) && !channels.some((c) => c.text.includes(t))) return null;
  }
  if (terms.every((t) => own.includes(t))) return { page };
  const hit = channels.find((c) => terms.some((t) => !own.includes(t) && c.text.includes(t)));
  return { page, matchedChannel: hit?.label };
}

const statusMatches = (p: CreatorReportPage, status: DashboardStatusFilter, now: Date): boolean => {
  if (status === 'active') return !p.revokedAt;
  if (status === 'deactivated') return !!p.revokedAt;
  if (status === 'locked') return isLocked(p, now);
  return true;
};

export function filterCreatorDashboards(
  pages: CreatorReportPage[],
  filters: DashboardFilters,
  now: Date = new Date(),
): FilteredDashboards {
  const terms = searchTerms(filters.q);
  const counts: Record<DashboardTypeFilter, number> = { all: 0, creator: 0, agency: 0 };
  const rows: DashboardRow[] = [];
  for (const page of pages) {
    if (!statusMatches(page, filters.status, now)) continue;
    const row = matchRow(page, terms);
    if (!row) continue;
    const type = page.pageType ?? 'creator';
    counts.all += 1;
    counts[type] += 1;
    if (filters.type === 'all' || filters.type === type) rows.push(row);
  }
  return { rows, counts };
}
